import React, { FC, useState, useCallback } from 'react';
import { Spinner, Tooltip } from '@chakra-ui/react';
import LeopartLoader from "../../../shared/LeopartLoader/LeopartLoader";
import {
  CaretDownIcon,
  ClockIcon,
  CalendarBlankIcon,
  WarningCircleIcon,
} from '@phosphor-icons/react';
import dayjs from 'dayjs';
import cx from 'classnames';
import GenericModalV2 from '../../../shared/GenericModal/GenericModal';
import CustomButton from '../../../shared/CustomButton/CustomButton';
import Typhography from '../../../shared/Typhography/Typhography';
import { useOccurrence } from '../../../hooks/useOccurrences';
import { useEndpoints } from '../../../hooks/useEndpoints';
import ConditioningStepsView from '../WorkoutBuilderModal/ConditioningSteps/ConditioningStepsView';
import {
  WORKOUT_CATEGORY,
  WORKOUT_CATEGORY_LABELS,
  WORKOUT_CATEGORY_COLORS,
  WORKOUT_TYPE,
  BLOCK_TYPE_COLORS,
  BLOCK_TYPE_LABELS,
  EXERCISE_METHOD,
  EXERCISE_METHOD_LABELS,
  LOAD_UNIT_LABELS,
  WORKOUT_LOG_STATUS,
} from '../../../enums/programming';
import { IWorkoutLog, IWorkout, IWorkoutDay, IBlock, IExerciseInBlock } from '../../../models/programming';
import { Paginated } from '../../../typings/Paginated';
import { IUser2 } from '../../../models/user copy';
import { formatFullName } from '../../../utils/functions';
import styles from './OccurrenceDetailModal.module.scss';

// ─── Constants ────────────────────────────────────────────────────────────────

const AVATAR_COLORS = ['#4a7cff', '#34d399', '#f59e0b', '#a78bfa', '#e53e3e', '#38bdf8', '#fb923c'];

const LOG_STATUS_STYLES: Record<WORKOUT_LOG_STATUS, { background: string; color: string; label: string }> = {
  [WORKOUT_LOG_STATUS.COMPLETED]: { background: '#d1fae5', color: '#065f46', label: 'Completato' },
  [WORKOUT_LOG_STATUS.COMPLETED_LATE]: { background: '#fef3c7', color: '#92400e', label: 'Completato tardi' },
  [WORKOUT_LOG_STATUS.IN_PROGRESS]: { background: '#dbeafe', color: '#1e40af', label: 'In corso' },
  [WORKOUT_LOG_STATUS.NOT_STARTED]: { background: '#f3f4f6', color: '#6b7280', label: 'Non iniziato' },
  [WORKOUT_LOG_STATUS.SKIPPED]: { background: '#fee2e2', color: '#991b1b', label: 'Saltato' },
};

// ─── Props ────────────────────────────────────────────────────────────────────

interface OccurrenceDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  eventId: string | undefined;
  workoutAssignmentId: string | undefined;
  onEdit: (event: any, workout: any) => void;
  onDelete: (event: any) => void;
  onCompile: (event: any, workout: any, athleteId?: string) => void;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function getExerciseName(ex: any): string {
  const exId = ex.exerciseId;
  const isPopulated = typeof exId === 'object' && exId !== null;
  const exerciseObj = isPopulated ? exId : ex.exercise;
  return exerciseObj?.name?.it || exerciseObj?.name?.en || (typeof exId === 'string' ? exId : '—');
}

function getInitials(user: IUser2 | string): string {
  if (typeof user === 'string') return '?';
  const first = user.firstName || '';
  const last = user.lastName || '';
  return `${first.charAt(0)}${last.charAt(0)}`.toUpperCase() || '?';
}

function getAvatarColor(index: number): string {
  return AVATAR_COLORS[index % AVATAR_COLORS.length];
}

// ─── AthleteLogRow ────────────────────────────────────────────────────────────

interface AthleteLogRowProps {
  athlete: IUser2 | string;
  athleteId: string;
  athleteName: string;
  athleteIndex: number;
  isCompiled: boolean;
  workoutAssignmentId: string;
  eventId: string;
  onCompile: () => void;
}

const AthleteLogRow: FC<AthleteLogRowProps> = ({
  athlete,
  athleteId,
  athleteName,
  athleteIndex,
  isCompiled,
  workoutAssignmentId,
  eventId,
  onCompile,
}) => {
  const [expanded, setExpanded] = useState(false);
  const [log, setLog] = useState<IWorkoutLog | null>(null);
  const [isLoadingLog, setIsLoadingLog] = useState(false);
  const { getAthleteLogs } = useEndpoints();

  const handleToggle = useCallback(async () => {
    if (!isCompiled) return;
    const next = !expanded;
    setExpanded(next);
    if (next && !log) {
      setIsLoadingLog(true);
      try {
        const { data } = await getAthleteLogs(athleteId, {
          workoutAssignmentId,
          // Filtra per eventId così per le assegnazioni ricorrenti
          // viene recuperato esattamente il log di questa occorrenza
          eventId,
          limit: 5,
        });
        const docs = (data as Paginated<IWorkoutLog> | undefined)?.docs ?? [];
        // Priorità: match esatto per eventId, fallback al primo disponibile
        const match = docs.find((l: IWorkoutLog) => l.eventId === eventId) ?? docs[0] ?? null;
        setLog(match);
      } catch {
        setLog(null);
      } finally {
        setIsLoadingLog(false);
      }
    }
  }, [expanded, isCompiled, log, athleteId, workoutAssignmentId, eventId, getAthleteLogs]);

  const logStatus = log?.status ?? (isCompiled ? WORKOUT_LOG_STATUS.COMPLETED : WORKOUT_LOG_STATUS.NOT_STARTED);
  const statusStyle = LOG_STATUS_STYLES[logStatus] ?? LOG_STATUS_STYLES[WORKOUT_LOG_STATUS.NOT_STARTED];

  return (
    <div className={cx(styles.athleteRow, { [styles.expanded]: expanded })}>
      <div className={styles.athleteHeader} onClick={handleToggle}>
        {/* Avatar */}
        <div
          className={styles.athleteAvatar}
          style={{ background: getAvatarColor(athleteIndex) }}
        >
          {getInitials(athlete)}
        </div>

        {/* Name */}
        <span className={styles.athleteName}>
          {athleteName}
        </span>

        {/* Status badge */}
        <span
          className={styles.athleteStatusBadge}
          style={{ background: statusStyle.background, color: statusStyle.color }}
        >
          {statusStyle.label}
        </span>

        {/* Meta: RPE, duration */}
        {log && (
          <div className={styles.athleteMeta}>
            {log.rpe != null && (
              <span className={styles.athleteMetaItem}>RPE {log.rpe}</span>
            )}
            {log.duration != null && (
              <span className={styles.athleteMetaItem}>
                <ClockIcon size={11} weight="bold" />
                {log.duration} min
              </span>
            )}
          </div>
        )}

        {/* Expand icon (only if compiled) */}
        {isCompiled && (
          <CaretDownIcon
            size={14}
            weight="bold"
            className={cx(styles.expandIcon, { [styles.rotated]: expanded })}
          />
        )}
      </div>

      {/* Log detail */}
      {isCompiled && expanded && (
        <div className={styles.athleteLog}>
          {isLoadingLog && (
            <div className={styles.logLoadingRow}>
              <Spinner size="sm" />
            </div>
          )}

          {!isLoadingLog && !log && (
            <Typhography size="regular-12" className="text-center py-3">
              Nessun log trovato
            </Typhography>
          )}

          {!isLoadingLog && log && (
            <>
              {log.exercises.map((exLog, exIdx) => (
                <div key={`${typeof exLog.exerciseId === 'object' ? (exLog.exerciseId as any)?._id : exLog.exerciseId}-${exIdx}`}>
                  <div className={styles.logExerciseTitle}>
                    {getExerciseName(exLog)}
                  </div>

                  {/* Set grid header */}
                  <div className={styles.setGridHeader}>
                    <span>Set</span>
                    <span>Reps</span>
                    <span>Carico</span>
                    <span>RPE</span>
                  </div>

                  {exLog.sets.map((setRow) => (
                    <div key={`set-${setRow.setNumber}`} className={styles.setGrid}>
                      <span className={styles.setCell}>{setRow.setNumber}</span>
                      <span className={styles.setCell}>{setRow.repsActual ?? '—'}</span>
                      <span className={styles.setCell}>
                        {setRow.loadActual != null
                          ? `${setRow.loadActual}${setRow.loadActualUnit ? ` ${setRow.loadActualUnit}` : ''}`
                          : setRow.weight != null
                          ? `${setRow.weight}${setRow.unit ? ` ${setRow.unit}` : ''}`
                          : '—'}
                      </span>
                      <span className={styles.setCell}>{setRow.rpe ?? '—'}</span>
                    </div>
                  ))}
                </div>
              ))}

              {/* Summary */}
              <div className={styles.logSummary}>
                {log.tonnage != null && (
                  <div className={styles.logSummaryItem}>
                    <span className={styles.logSummaryLabel}>Tonnage</span>
                    <span className={styles.logSummaryValue}>{log.tonnage} kg</span>
                  </div>
                )}
                {log.duration != null && (
                  <div className={styles.logSummaryItem}>
                    <span className={styles.logSummaryLabel}>Durata</span>
                    <span className={styles.logSummaryValue}>{log.duration} min</span>
                  </div>
                )}
                {log.sRPE != null && (
                  <div className={styles.logSummaryItem}>
                    <span className={styles.logSummaryLabel}>sRPE</span>
                    <span className={styles.logSummaryValue}>{log.sRPE}</span>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      )}

      {/* Not compiled action */}
      {!isCompiled && (
        <div className={styles.notCompiledRow}>
          <Typhography size="regular-12" className="text-muted">
            Non completato
          </Typhography>
          <CustomButton color="secondary" onClick={onCompile}>
            Compila
          </CustomButton>
        </div>
      )}
    </div>
  );
};

// ─── Main component ───────────────────────────────────────────────────────────

const OccurrenceDetailModal: FC<OccurrenceDetailModalProps> = ({
  isOpen,
  onClose,
  eventId,
  workoutAssignmentId,
  onEdit,
  onDelete,
  onCompile,
}) => {
  const { data: occurrence, isLoading } = useOccurrence(
    workoutAssignmentId,
    eventId,
    isOpen,
  );

  const workout: IWorkout | null = (() => {
    if (!occurrence) return null;
    // workout may be under .workout or under .workoutId (populated object)
    const w = (occurrence as any).workout ?? (
      typeof (occurrence as any).workoutId === 'object' ? (occurrence as any).workoutId : null
    );
    return w ?? null;
  })();
  const compiledIds: string[] = (occurrence as any)?.compiledIds ?? [];
  const isImmutable = compiledIds.length > 0;

  // Collect athletes from users or userIds
  const athletes: (IUser2 | string)[] = (() => {
    const raw = (occurrence as any);
    if (!raw) return [];
    if (Array.isArray(raw.users) && raw.users.length > 0) return raw.users;
    if (Array.isArray(raw.userIds) && raw.userIds.length > 0) return raw.userIds;
    return [];
  })();

  /** Risolve l'ID di un atleta, sia oggetto che stringa */
  const getAthleteId = (a: IUser2 | string): string =>
    typeof a === 'string' ? a : a._id;

  const getAthleteName = (a: IUser2 | string): string =>
    typeof a === 'string' ? a.slice(-6) : formatFullName(a.firstName, a.lastName);

  const completedCount = athletes.filter((a) => compiledIds.includes(getAthleteId(a))).length;

  const categoryColor = workout?.workoutCategory
    ? WORKOUT_CATEGORY_COLORS[workout.workoutCategory as WORKOUT_CATEGORY]
    : '#4a7cff';

  const categoryLabel = workout?.workoutCategory
    ? WORKOUT_CATEGORY_LABELS[workout.workoutCategory as WORKOUT_CATEGORY]
    : null;

  const startDate = occurrence?.startDate ? dayjs(occurrence.startDate).format('DD MMM YYYY') : null;

  return (
    <GenericModalV2
      isOpen={isOpen}
      onClose={onClose}
      size="xl"
      Header={
        <div style={{ paddingBottom: 12 }}>
          <Typhography size="bold-20">
            {workout?.title || (occurrence as any)?.name || 'Dettaglio allenamento'}
          </Typhography>

          {/* Meta badges */}
          <div className={styles.headerMeta} style={{ marginTop: 8 }}>
            {categoryLabel && (
              <span
                className={styles.metaBadge}
                style={{ background: categoryColor }}
              >
                {categoryLabel}
              </span>
            )}

            {workout?.type && (
              <span className={styles.metaTagGray}>
                {workout.type === WORKOUT_TYPE.MULTI_DAY ? 'Multi-giorno' : 'Giorno singolo'}
              </span>
            )}

            {workout?.estimatedDuration && (
              <span className={styles.metaDuration}>
                <ClockIcon size={12} weight="bold" />
                ~{workout.estimatedDuration} min
              </span>
            )}

            {startDate && (
              <span className={styles.metaDuration}>
                <CalendarBlankIcon size={12} weight="bold" />
                {startDate}
              </span>
            )}
          </div>

          {/* Action buttons */}
          <div className={styles.actions}>
            {isImmutable ? (
              <Tooltip.Root openDelay={200}>
                <Tooltip.Trigger asChild>
                  <span>
                    <CustomButton
                      color="outline"
                      disabled
                    >
                      Modifica
                    </CustomButton>
                  </span>
                </Tooltip.Trigger>
                <Tooltip.Positioner>
                  <Tooltip.Content>
                    Non modificabile: alcuni atleti hanno già compilato
                  </Tooltip.Content>
                </Tooltip.Positioner>
              </Tooltip.Root>
            ) : (
              <CustomButton
                color="outline"
                onClick={() => occurrence && onEdit(occurrence, workout)}
              >
                Modifica
              </CustomButton>
            )}

            <CustomButton
              color="outline"
              onClick={() => occurrence && onDelete(occurrence)}
            >
              Elimina
            </CustomButton>

            <CustomButton
              color="secondary"
              onClick={() => occurrence && onCompile(occurrence, workout)}
            >
              Compila
            </CustomButton>
          </div>
        </div>
      }
    >
      {isLoading && (
        <div className={styles.loadingState}>
          <LeopartLoader />
        </div>
      )}

      {!isLoading && occurrence && (
        <>
          {/* ── Workout content ── */}
          {workout && (
            <div style={{ marginBottom: 20 }}>
              <div className={styles.sectionTitle}>Contenuto scheda</div>

              {(() => {
                const dayNumber = (occurrence as any)?.dayNumber;
                const daysToShow: IWorkoutDay[] = dayNumber && (workout?.days?.length ?? 0) > 1
                  ? (workout?.days ?? []).filter((d: IWorkoutDay) => d.dayNumber === dayNumber)
                  : workout?.days ?? [];
                return daysToShow;
              })().map((day: IWorkoutDay, dayIdx: number) => (
                <div key={dayIdx} className={styles.daySection}>
                  <div className={styles.dayHeader}>
                    <Typhography size="bold-14">
                      Giorno {day.dayNumber}{day.title ? ` — ${day.title}` : ''}
                    </Typhography>
                  </div>

                  {day.blocks.map((block: IBlock, blockIdx: number) => {
                    const blockColor = block.color || BLOCK_TYPE_COLORS[block.type] || '#757575';
                    return (
                      <div
                        key={blockIdx}
                        className={styles.blockContainer}
                        style={{ '--block-color': blockColor } as React.CSSProperties}
                      >
                        <div className={styles.blockBorder} />
                        <div className={styles.blockBody}>
                          <div className={styles.blockHeader}>
                            <Typhography size="bold-14">{block.title}</Typhography>
                            <span
                              className={styles.blockTypeBadge}
                              style={{
                                background: blockColor + '18',
                                color: blockColor,
                                border: `1px solid ${blockColor}30`,
                              }}
                            >
                              {BLOCK_TYPE_LABELS[block.type] || block.type}
                            </span>
                          </div>

                          {block.exercises.map((ex: IExerciseInBlock, exIdx: number) => {
                            // Mezzo endurance = presenza di step (BUG A): i dati salvati
                            // non hanno più method='steps'.
                            const isSteps = Array.isArray((ex as any).steps) && (ex as any).steps.length > 0;
                            return (
                            <div key={exIdx}>
                            <div className={styles.exerciseRow}>
                              <span className={styles.exerciseOrder}>{ex.order + 1}</span>
                              <span className={styles.exerciseName}>{getExerciseName(ex)}</span>
                              {!isSteps && (
                                <span className={styles.exerciseDetail}>
                                  {ex.sets > 0 ? `${ex.sets}×${ex.reps || '—'}` : ''}
                                </span>
                              )}

                              {!isSteps && ex.loads && ex.loads.length > 0 && ex.loads[0].value > 0 && (
                                <span className={styles.exerciseLoad}>
                                  {ex.loads[0].value}{' '}
                                  {LOAD_UNIT_LABELS[ex.loads[0].unit] ?? ex.loads[0].unit}
                                </span>
                              )}

                              {!isSteps && ex.rest && (
                                <span className={styles.exerciseRest}>
                                  <ClockIcon size={10} weight="bold" />
                                  {ex.rest}
                                </span>
                              )}

                              {ex.method && ex.method !== EXERCISE_METHOD.STANDARD && (
                                <span className={styles.methodBadge}>
                                  {EXERCISE_METHOD_LABELS[ex.method] ?? ex.method}
                                </span>
                              )}
                            </div>
                            {isSteps && <ConditioningStepsView steps={(ex as any).steps} />}
                            </div>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
                </div>
              ))}
            </div>
          )}

          <div className={styles.separator} />

          {/* ── Compilation status ── */}
          <div>
            <div className={styles.compilationHeader}>
              <Typhography size="bold-14">Stato Compilazione</Typhography>
              <span className={styles.compilationCount}>
                {completedCount}/{athletes.length} completati
              </span>
            </div>

            {athletes.length > 0 && (
              <div className={styles.progressBarBg}>
                <div
                  className={styles.progressBarFill}
                  style={{
                    width: `${athletes.length > 0 ? (completedCount / athletes.length) * 100 : 0}%`,
                  }}
                />
              </div>
            )}

            {athletes.length === 0 && (
              <div className="d-flex align-items-center gap-2 py-3">
                <WarningCircleIcon size={16} weight="bold" style={{ color: 'var(--c-text-3)' }} />
                <Typhography size="regular-12">Nessun atleta assegnato</Typhography>
              </div>
            )}

            {athletes.map((athlete, idx) => {
              const aId = getAthleteId(athlete);
              const aName = getAthleteName(athlete);
              const compiled = compiledIds.includes(aId);
              return (
                <AthleteLogRow
                  key={aId}
                  athlete={athlete}
                  athleteId={aId}
                  athleteName={aName}
                  athleteIndex={idx}
                  isCompiled={compiled}
                  workoutAssignmentId={workoutAssignmentId!}
                  eventId={eventId!}
                  onCompile={() => occurrence && onCompile(occurrence, workout, aId)}
                />
              );
            })}
          </div>
        </>
      )}
    </GenericModalV2>
  );
};

export default OccurrenceDetailModal;
